import React, { useEffect, useState } from 'react';
import { activateAuthLayout } from '../../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
    Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input,
} from 'reactstrap';
import SweetAlert from 'react-bootstrap-sweetalert';

const DATA = gql`
    query getData {
        Attributes {
            AttributeId
            Handle
            Name
            Values {
                AttributeValueId
                Value
            }
        }
    }
`;

const CREATE_ATTRIBUTE = gql`
    mutation createAttribute($input: AttributeInput) {
        createAttribute(input: $input) {
            AttributeId
            Handle
            Name
            Values {
                AttributeValueId
                Value
            }
        }
    }
`;
const UPDATE_ATTRIBUTE = gql`
    mutation updateAttribute($input: AttributeInput) {
        updateAttribute(input: $input) {
            AttributeId
            Handle
            Name
        }
    }
`;
const DELETE_ATTRIBUTE = gql`
    mutation deleteAttribute($AttributeId: Int!) {
        deleteAttribute(AttributeId: $AttributeId) {
            AttributeId
        }
    }
`;
const CREATE_VALUE = gql`
    mutation createAttributeValue($input: AttributeValueInput) {
        createAttributeValue(input: $input) {
            AttributeValueId
            AttributeId
            Value
        }
    }
`;
const UPDATE_VALUE = gql`
    mutation updateAttributeValue($input: AttributeValueInput) {
        updateAttributeValue(input: $input) {
            AttributeValueId
            Value
        }
    }
`;
const DELETE_VALUE = gql`
    mutation deleteAttributeValue($AttributeValueId: Int!) {
        deleteAttributeValue(AttributeValueId: $AttributeValueId) {
            AttributeValueId
        }
    }
`;

const styles = {
    attrRow: {
        marginBottom: 8,
    },
    attrHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
    },
    attrHeaderLabel: {
        cursor: 'pointer',
        fontWeight: 500,
    },
    attrBody: {
        border: '1px solid #dee2e6',
        borderTop: 'none',
        borderRadius: '0 0 4px 4px',
        padding: '12px',
        background: '#fff',
    },
    valueRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 8px',
        marginBottom: '4px',
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
    },
};

const AttributeModal = ({ attribute, onSave, onClose }) => {
    const [handle, setHandle] = useState(attribute?.Handle ?? '');
    const [name, setName] = useState(attribute?.Name ?? '');

    const save = (e) => {
        e?.preventDefault();
        if (!handle.trim() || !name.trim()) return;
        Promise.resolve(onSave(name.trim(), handle.trim())).then(onClose);
    };

    return (
        <>
            <ModalBody>
                <Form onSubmit={save}>
                    <FormGroup>
                        <Label>Handle <small className="text-danger">*</small></Label>
                        <Input
                            type="text"
                            value={handle}
                            onChange={e => setHandle(e.target.value)}
                            autoFocus
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Attribute Name <small className="text-danger">*</small></Label>
                        <Input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={save} disabled={!handle.trim() || !name.trim()}>Save</Button>
            </ModalFooter>
        </>
    );
};

const ValueModal = ({ value, onSave, onClose }) => {
    const [val, setVal] = useState(value?.Value ?? '');

    const save = (e) => {
        e?.preventDefault();
        if (!val.trim()) return;
        Promise.resolve(onSave(val.trim())).then(onClose);
    };

    return (
        <>
            <ModalBody>
                <Form onSubmit={save}>
                    <FormGroup>
                        <Label>Value <small className="text-danger">*</small></Label>
                        <Input
                            type="text"
                            value={val}
                            onChange={e => setVal(e.target.value)}
                            autoFocus
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={save} disabled={!val.trim()}>Save</Button>
            </ModalFooter>
        </>
    );
};

const AttributeList = ({ activateAuthLayout }) => {
    useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

    const { data, loading } = useQuery(DATA);
    const [createAttribute] = useMutation(CREATE_ATTRIBUTE);
    const [updateAttribute] = useMutation(UPDATE_ATTRIBUTE);
    const [deleteAttribute] = useMutation(DELETE_ATTRIBUTE);
    const [createAttributeValue] = useMutation(CREATE_VALUE);
    const [updateAttributeValue] = useMutation(UPDATE_VALUE);
    const [deleteAttributeValue] = useMutation(DELETE_VALUE);

    const [expanded, setExpanded] = useState({});
    const [modal, setModal] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const closeModal = () => setModal(null);

    const toggleExpanded = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    const handleDeleteConfirm = () => {
        const { type, item } = deleteTarget;
        const promise = type === 'attribute'
            ? deleteAttribute({ variables: { AttributeId: item.AttributeId }, refetchQueries: ['getData'] })
            : deleteAttributeValue({ variables: { AttributeValueId: item.AttributeValueId }, refetchQueries: ['getData'] });
        promise.then(() => setDeleteTarget(null));
    };

    const attributes = data?.Attributes ?? [];

    return (
        <>
            {deleteTarget && (
                <SweetAlert
                    warning
                    title="Confirm"
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteTarget(null)}
                    showCancel
                    confirmBtnText="Delete"
                    cancelBtnText="Cancel"
                    confirmBtnBsStyle="danger"
                    cancelBtnBsStyle="secondary"
                >
                    {deleteTarget.type === 'attribute'
                        ? `Delete attribute "${deleteTarget.item.Name}"? This will remove it from all products.`
                        : `Delete value "${deleteTarget.item.Value}"?`
                    }
                </SweetAlert>
            )}

            <Modal isOpen={!!modal} toggle={closeModal}>
                <ModalHeader toggle={closeModal}>
                    {modal?.type === 'attribute'
                        ? (modal.item ? 'Edit Attribute' : 'Create Attribute')
                        : (modal?.item ? 'Edit Value' : 'Add Value')
                    }
                </ModalHeader>
                {modal?.type === 'attribute' && (
                    <AttributeModal
                        key={modal.item?.AttributeId ?? '__create__'}
                        attribute={modal.item}
                        onClose={closeModal}
                        onSave={(name, handle) => modal.item
                            ? updateAttribute({ variables: { input: { AttributeId: modal.item.AttributeId, Handle: handle, Name: name } }, refetchQueries: ['getData'] })
                            : createAttribute({ variables: { input: { Handle: handle, Name: name } }, refetchQueries: ['getData'] }).then(({ data }) => {
                                setExpanded(prev => ({ ...prev, [data.createAttribute.AttributeId]: true }));
                            })
                        }
                    />
                )}
                {modal?.type === 'value' && (
                    <ValueModal
                        key={modal.item?.AttributeValueId ?? '__create__'}
                        value={modal.item}
                        onClose={closeModal}
                        onSave={(val) => modal.item
                            ? updateAttributeValue({ variables: { input: { AttributeValueId: modal.item.AttributeValueId, Value: val } }, refetchQueries: ['getData'] })
                            : createAttributeValue({ variables: { input: { AttributeId: modal.attributeId, Value: val } }, refetchQueries: ['getData'] })
                        }
                    />
                )}
            </Modal>

            <div className="content">
                <div className="container-fluid">
                    <div className="page-title-box">
                        <Row className="align-items-center">
                            <Col sm="6">
                                <h4 className="page-title">Attributes</h4>
                            </Col>
                            <Col sm="6" className="text-right">
                                <Button color="success" onClick={() => setModal({ type: 'attribute', item: null })}>
                                    Create New Attribute
                                </Button>
                            </Col>
                        </Row>
                    </div>
                    <Row>
                        <Col lg="6">
                            <Card>
                                <CardBody>
                                    {loading ? null : attributes.length === 0 ? 'No product attributes exist.' : attributes.map(attr => (
                                        <div key={attr.AttributeId} style={styles.attrRow}>
                                            <div style={{ ...styles.attrHeader, borderRadius: expanded[attr.AttributeId] ? '4px 4px 0 0' : '4px' }}>
                                                <span
                                                    style={styles.attrHeaderLabel}
                                                    onClick={() => toggleExpanded(attr.AttributeId)}
                                                >
                                                    <i className={`fas fa-caret-${expanded[attr.AttributeId] ? 'down' : 'right'} mr-1`} />
                                                    {attr.Name}
                                                    <small className="text-muted ml-1">({attr.Values.length})</small>
                                                </span>
                                                <div>
                                                    <Button color="info" size="sm" className="mr-2"
                                                        onClick={() => setModal({ type: 'attribute', item: attr })}>
                                                        Edit
                                                    </Button>
                                                    <Button color="danger" size="sm"
                                                        onClick={() => setDeleteTarget({ type: 'attribute', item: attr })}>
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                            {expanded[attr.AttributeId] && (
                                                <div style={styles.attrBody}>
                                                    {attr.Values.length > 0 && (
                                                        <div className="mb-2">
                                                            {attr.Values.map(v => (
                                                                <div key={v.AttributeValueId} style={styles.valueRow}>
                                                                    <span>{v.Value}</span>
                                                                    <div>
                                                                        <Button color="info" size="sm" className="mr-2"
                                                                            onClick={() => setModal({ type: 'value', item: v, attributeId: attr.AttributeId })}>
                                                                            Edit
                                                                        </Button>
                                                                        <Button color="danger" size="sm"
                                                                            onClick={() => setDeleteTarget({ type: 'value', item: v })}>
                                                                            Delete
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="text-right">
                                                        <Button color="success" size="sm"
                                                            onClick={() => setModal({ type: 'value', item: null, attributeId: attr.AttributeId })}>
                                                            Add Value
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    );
};

export default connect(null, { activateAuthLayout })(AttributeList);
